/**
 * SKELETON
 * Briques génériques pour construire un skeleton de chargement (à la place d'un
 * spinner) : bloc rectangulaire/rond pulsé (`Skeleton`) + reflet qui balaie
 * (`SkeletonShine`) + hook `useSkeletonPulse` pour piloter un pulse "opacity"
 * décalé/aléatoire sur des éléments custom (ex: segments SVG d'un skeleton
 * plus spécifique, cf dashboard-results-topics-skeleton.widget.tsx).
 *
 * Chaque instance a un timing légèrement randomisé pour éviter que plusieurs
 * blocs sur une même page pulsent parfaitement en synchro (moins mécanique).
 */
import { CSSProperties, useState } from "react"

let uid = 0

interface SkeletonPulseOptions {
  minOpacity?: number
  maxOpacity?: number
  minDuration?: number
  maxDuration?: number
}

//Génère un pulse opacity décalé pour `count` éléments (rects, paths, divs...).
//Retourne un <style> à injecter une fois (keyframes) + une fonction itemStyle(i)
//à répartir sur chaque élément.
export function useSkeletonPulse(count: number, options: SkeletonPulseOptions = {}) {
  const { minOpacity = 0.35, maxOpacity = 0.75, minDuration = 1.1, maxDuration = 2.3 } = options

  const [{ id, items }] = useState(() => {
    const id = "skp" + uid++
    const items = Array.from({ length: count }, () => ({
      lo: minOpacity + Math.random() * 0.15,
      hi: maxOpacity - Math.random() * 0.15,
      dur: minDuration + Math.random() * (maxDuration - minDuration),
      delay: -(Math.random() * maxDuration)
    }))
    return { id, items }
  })

  const css = items
    .map((a, i) => `@keyframes ${id}-${i}{0%,100%{opacity:${a.lo.toFixed(2)}}50%{opacity:${a.hi.toFixed(2)}}}`)
    .join("\n")

  function itemStyle(i: number): CSSProperties {
    const item = items[i]
    return {
      animation: `${id}-${i} ${item.dur.toFixed(2)}s ease-in-out infinite`,
      animationDelay: `${item.delay.toFixed(2)}s`
    }
  }

  return { keyframes: <style>{css}</style>, itemStyle }
}

//Style de pulsation à poser sur tout un bloc en chargement (keyframes déclarées une fois
//dans components.css). Timing randomisé par instance pour que plusieurs cards en
//chargement en même temps ne pulsent pas en synchro.
//- "scale" (défaut) : léger zoom avant/arrière (cardBreathe)
//- "glow" : pulsation d'ombre portée, sans bouger les bords de la card (cardGlow)
export function useCardBreathe(variant: "scale" | "glow" = "scale"): CSSProperties {
  const [breathe] = useState(() => ({
    duration: 1.8 + Math.random() * 0.8,
    delay: -(Math.random() * 2.6)
  }))

  const animationName = variant === "glow" ? "cardGlow" : "cardBreathe"

  return {
    animation: `${animationName} ${breathe.duration.toFixed(2)}s ease-in-out infinite`,
    animationDelay: `${breathe.delay.toFixed(2)}s`
  }
}

interface SkeletonProps {
  width?: number | string
  height?: number | string
  borderRadius?: number | string
  isCircle?: boolean
  isInline?: boolean //display:inline-block plutôt que block (pour aligner plusieurs Skeleton sur une ligne)
  className?: string
  style?: CSSProperties
}

//Bloc de placeholder générique (une ligne de texte, un avatar, une carte...) : gris + pulse opacity
export default function Skeleton(props: SkeletonProps) {
  const { itemStyle } = useSkeletonPulse(1)

  return (
    <div
      className={"skeleton-block" + (props.className ? " " + props.className : "")}
      style={{
        display: props.isInline ? "inline-block" : "block",
        width: props.width ?? "100%",
        height: props.height ?? 14,
        borderRadius: props.isCircle ? "50%" : (props.borderRadius ?? 4),
        ...itemStyle(0),
        ...props.style
      }}
    />
  )
}

//Reflet qui balaie de gauche à droite — à poser sur un parent `position:relative; overflow:hidden`
export function SkeletonShine() {
  return <div className="skeleton-shine" />
}
